import React from "react";
import es from "./translations/es";
import fi from "./translations/fi";
import fr from "./translations/fr";
import it from "./translations/it";
import pt from "./translations/pt";
import de from "./translations/de";

export const DEFAULT_LOCALE = "en";
export const SITE_URL = "https://tiply.ee";

const availability = {
    en: {
        question: "Where is Tiply available?",
        answer: "Tiply supports eligible businesses in all 21 EU euro-area countries, subject to onboarding, identity verification, compatible EUR payout details, payment-provider availability, and local law.",
    },
    es: {
        question: "¿Tiply está disponible en España?",
        answer: "Sí. Tiply admite empresas elegibles en España y en los 21 países de la zona euro de la UE, sujeto a incorporación, verificación de identidad, datos de pago en EUR compatibles, disponibilidad del proveedor de pagos y normativa local.",
    },
    fi: {
        question: "Onko Tiply saatavilla Suomessa?",
        answer: "Kyllä. Tiply palvelee soveltuvia yrityksiä Suomessa ja kaikissa 21:ssä EU:n euroalueen maassa. Saatavuus edellyttää käyttöönottoa, henkilöllisyyden varmentamista, yhteensopivia euromääräisiä maksutietoja, maksupalvelun saatavuutta ja paikallisten lakien noudattamista.",
    },
    fr: {
        question: "Tiply est-il disponible en France ?",
        answer: "Oui. Tiply accompagne les entreprises éligibles en France et dans les 21 pays de la zone euro de l’UE, sous réserve de l’intégration, de la vérification d’identité, de coordonnées de versement en EUR compatibles, de la disponibilité du prestataire de paiement et du droit local.",
    },
    it: {
        question: "Tiply è disponibile in Italia?",
        answer: "Sì. Tiply supporta le aziende idonee in Italia e in tutti i 21 paesi dell’area euro dell’UE, previa attivazione, verifica dell’identità, disponibilità di coordinate compatibili per pagamenti in EUR, disponibilità del fornitore di pagamento e rispetto delle norme locali.",
    },
    pt: {
        question: "A Tiply está disponível em Portugal?",
        answer: "Sim. A Tiply aceita empresas elegíveis em Portugal e nos 21 países da zona euro da UE, sujeita a integração, verificação de identidade, dados compatíveis para pagamentos em EUR, disponibilidade do prestador de pagamentos e legislação local.",
    },
    de: {
        question: "Ist Tiply in Deutschland verfügbar?",
        answer: "Ja. Tiply unterstützt geeignete Unternehmen in Deutschland und allen 21 EU-Ländern des Euroraums – vorbehaltlich Onboarding, Identitätsprüfung, kompatibler EUR-Auszahlungsdaten, Verfügbarkeit des Zahlungsdienstleisters und lokaler Vorschriften.",
    },
};

export const LOCALE_CONFIG = {
    en: {
        code: "en",
        hreflang: "en",
        path: "/",
        nativeName: "English",
        shortLabel: "EN",
        country: "Europe",
        seo: {
            title: "Digital Tipping for Restaurants & Service Teams | Tiply",
            description: "Accept QR tips by Apple Pay, Google Pay or card, split team tips fairly, collect feedback and grow reviews with Tiply across the euro area.",
        },
        teamExample: "Your team earns €600 in tips this week. Alice worked 30 hours, Bob 20 hours, and Carol 10 hours. Tiply allocates €300 to Alice, €200 to Bob, and €100 to Carol before applicable fees and taxes, then routes the payouts to their bank accounts.",
        availabilityFaq: availability.en,
    },
    es: {
        code: "es",
        hreflang: "es-ES",
        path: "/es/",
        nativeName: "Español",
        shortLabel: "ES",
        country: "Spain",
        seo: {
            title: "Propinas digitales con QR para restaurantes en España | Tiply",
            description: "Acepta propinas digitales por QR con Apple Pay, Google Pay o tarjeta, reparte las propinas del equipo y consigue reseñas con Tiply en España.",
        },
        teamExample: "Tu equipo consigue 600 € en propinas esta semana. Alice trabajó 30 horas, Bob 20 y Carol 10. Tiply asigna 300 € a Alice, 200 € a Bob y 100 € a Carol antes de comisiones e impuestos aplicables, y envía los pagos a sus cuentas bancarias.",
        availabilityFaq: availability.es,
    },
    fi: {
        code: "fi",
        hreflang: "fi-FI",
        path: "/fi/",
        nativeName: "Suomi",
        shortLabel: "FI",
        country: "Finland",
        seo: {
            title: "Digitaalinen tippaus QR-koodilla ravintoloille Suomessa | Tiply",
            description: "Vastaanota QR-tipit Apple Paylla, Google Paylla tai kortilla, jaa tiimin tipit reilusti ja kerää palautetta Tiplyllä Suomessa.",
        },
        teamExample: "Tiimisi saa tällä viikolla 600 € tippejä. Alice työskenteli 30 tuntia, Bob 20 ja Carol 10. Tiply kohdistaa Alicelle 300 €, Bobille 200 € ja Carolille 100 € ennen sovellettavia maksuja ja veroja ja välittää maksut heidän pankkitileilleen.",
        availabilityFaq: availability.fi,
    },
    fr: {
        code: "fr",
        hreflang: "fr-FR",
        path: "/fr/",
        nativeName: "Français",
        shortLabel: "FR",
        country: "France",
        seo: {
            title: "Pourboires digitaux par QR code pour restaurants en France | Tiply",
            description: "Acceptez les pourboires par QR code avec Apple Pay, Google Pay ou carte, répartissez-les équitablement et développez vos avis avec Tiply en France.",
        },
        teamExample: "Votre équipe reçoit 600 € de pourboires cette semaine. Alice a travaillé 30 heures, Bob 20 et Carol 10. Tiply attribue 300 € à Alice, 200 € à Bob et 100 € à Carol avant les frais et impôts applicables, puis verse les montants sur leurs comptes bancaires.",
        availabilityFaq: availability.fr,
    },
    it: {
        code: "it",
        hreflang: "it-IT",
        path: "/it/",
        nativeName: "Italiano",
        shortLabel: "IT",
        country: "Italy",
        seo: {
            title: "Mance digitali con QR code per ristoranti in Italia | Tiply",
            description: "Accetta mance digitali via QR con Apple Pay, Google Pay o carta, dividile in modo equo e raccogli recensioni con Tiply in Italia.",
        },
        teamExample: "Questa settimana il team riceve 600 € di mance. Alice ha lavorato 30 ore, Bob 20 e Carol 10. Tiply assegna 300 € ad Alice, 200 € a Bob e 100 € a Carol prima delle commissioni e imposte applicabili, quindi invia i pagamenti ai loro conti bancari.",
        availabilityFaq: availability.it,
    },
    pt: {
        code: "pt",
        hreflang: "pt-PT",
        path: "/pt/",
        nativeName: "Português",
        shortLabel: "PT",
        country: "Portugal",
        seo: {
            title: "Gorjetas digitais por QR Code para restaurantes em Portugal | Tiply",
            description: "Aceite gorjetas por QR Code com Apple Pay, Google Pay ou cartão, distribua-as pela equipa e obtenha avaliações com a Tiply em Portugal.",
        },
        teamExample: "Esta semana, a equipa recebe 600 € em gorjetas. Alice trabalhou 30 horas, Bob 20 e Carol 10. A Tiply atribui 300 € a Alice, 200 € a Bob e 100 € a Carol antes das taxas e impostos aplicáveis e encaminha os pagamentos para as respetivas contas bancárias.",
        availabilityFaq: availability.pt,
    },
    de: {
        code: "de",
        hreflang: "de-DE",
        path: "/de/",
        nativeName: "Deutsch",
        shortLabel: "DE",
        country: "Germany",
        seo: {
            title: "Digitales Trinkgeld per QR-Code für Restaurants in Deutschland | Tiply",
            description: "Digitales Trinkgeld per QR-Code mit Apple Pay, Google Pay oder Karte annehmen, fair im Team verteilen und Bewertungen mit Tiply gewinnen.",
        },
        teamExample: "Ihr Team erhält diese Woche 600 € Trinkgeld. Alice arbeitete 30 Stunden, Bob 20 und Carol 10. Tiply ordnet Alice 300 €, Bob 200 € und Carol 100 € vor anwendbaren Gebühren und Steuern zu und leitet die Auszahlungen an ihre Bankkonten weiter.",
        availabilityFaq: availability.de,
    },
};

export const LOCALE_ORDER = ["en", "es", "fi", "fr", "it", "pt", "de"];

const translations = { es, fi, fr, it, pt, de };

export function translateText(locale, source) {
    if (!source || locale === DEFAULT_LOCALE) return source;
    return translations[locale]?.[source] || source;
}

const TRANSLATED_PROPS = new Set([
    "alt",
    "answer",
    "aria-label",
    "desc",
    "from",
    "label",
    "message",
    "placeholder",
    "question",
    "quote",
    "role",
    "title",
]);

function translateString(locale, value) {
    const trimmed = value.trim();
    if (!trimmed) return value;
    const translated = translateText(locale, trimmed);
    if (translated === trimmed) return value;
    const start = value.match(/^\s*/)?.[0] || "";
    const end = value.match(/\s*$/)?.[0] || "";
    return `${start}${translated}${end}`;
}

function translateNode(locale, node) {
    if (typeof node === "string") return translateString(locale, node);
    if (Array.isArray(node)) return node.map((child) => translateNode(locale, child));
    if (!React.isValidElement(node)) return node;

    const props = {};
    for (const prop of TRANSLATED_PROPS) {
        if (typeof node.props[prop] === "string") {
            props[prop] = translateString(locale, node.props[prop]);
        }
    }
    if (node.props.children !== undefined) {
        props.children = React.Children.map(node.props.children, (child) => translateNode(locale, child));
    }
    return React.cloneElement(node, props);
}

export function LocalizedContent({ locale = DEFAULT_LOCALE, children }) {
    if (locale === DEFAULT_LOCALE) return children;
    return translateNode(locale, children);
}
